import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { cn } from "lib/utils";
import PageHeader from "shared/PageHeader";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { FILLED_INPUT, FORM_CONTROL } from "shared/tableStyles";
import { fetchEvaluationEmployees, fetchEvaluationOverview } from "./api/evaluate";
import WeekMatrix from "./ui/WeekMatrix";
import { EvaluationEmployee } from "./modal/evaluate_context";
import EmployeeEvaluationDetails from "./EmployeeEvaluationDetails";

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

const SCOPE_LABEL: Record<string, string> = { all: "All employees", team: "Your team", self: "You" };

export default function EmployeeEvaluationPage() {
  const { employeeId } = useParams();
  const [employees, setEmployees] = useState<EvaluationEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("self");
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(employeeId ? Number(employeeId) : null);
  const [tab, setTab] = useState<"evaluate" | "week">("evaluate");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [counts, setCounts] = useState({ never: 0, stale: 0, days: 14 });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await fetchEvaluationEmployees();
        setScope(data.scope || "team");
        setEmployees(data.employees || []);
      } catch {
        setMessage("Unable to load employees for evaluation.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Banner counts: how many employees were never evaluated, or not within the overdue window.
  useEffect(() => {
    fetchEvaluationOverview()
      .then((o) => setCounts({
        never: o.employees.filter((e) => e.tier === "never").length,
        stale: o.employees.filter((e) => e.tier === "stale").length,
        days: o.overdue_days,
      }))
      .catch(() => undefined); // the banner is a nicety; the page works without it
  }, []);

  // Open the first employee by default so the right pane is never empty.
  useEffect(() => {
    if (employees.length > 0 && !employees.some((e) => e.id === selectedId)) setSelectedId(employees[0].id);
  }, [employees, selectedId]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () => (q ? employees.filter((e) => `${e.name} ${e.employee_code} ${e.email}`.toLowerCase().includes(q)) : employees),
    [employees, q],
  );
  const selected = employees.find((e) => e.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Employee Evaluation"
        description="Scored performance evaluations. Problems raised with an employee are logged under Employee Meetings."
        badge={<StatusPill tone="neutral" size="sm">{SCOPE_LABEL[scope] ?? scope}</StatusPill>}
      />

      {message && <Alert tone="danger">{message}</Alert>}

      {(counts.never > 0 || counts.stale > 0) && (
        <Alert tone="warning" role="status" className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          {counts.never > 0 && (
            <span><strong>{counts.never}</strong> {counts.never === 1 ? "employee has" : "employees have"} never been evaluated.</span>
          )}
          {counts.stale > 0 && (
            <span><strong>{counts.stale}</strong> {counts.stale === 1 ? "employee has" : "employees have"} not been evaluated in over {counts.days} days.</span>
          )}
          <button type="button" onClick={() => { setOverdueOnly(true); setTab("week"); }} className="underline font-medium">Show</button>
        </Alert>
      )}

      <div className="flex items-end gap-1 border-b border-border" role="tablist">
        {([["evaluate", "Evaluate"], ["week", "Week overview"]] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => { setTab(id); if (id === "evaluate") setOverdueOnly(false); }}
            className={cn(
              "px-4 py-2.5 text-sm rounded-t-lg transition-colors -mb-px border-b-2",
              tab === id ? "border-primary text-foreground bg-primary/10" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "week" && (
        <WeekMatrix
          key={overdueOnly ? "overdue" : "all"}
          initialOverdueOnly={overdueOnly}
          onOpenEmployee={(id) => { setSelectedId(id); setOverdueOnly(false); setTab("evaluate"); }}
        />
      )}

      {tab === "week" ? null : loading && employees.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : employees.length === 0 ? (
        !message && (
          <div className="py-16 text-center text-sm text-muted-foreground bg-surface border border-dashed border-border rounded-xl">
            No employees available for evaluation.
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-5 items-start">
          {/* Mobile: a select; desktop: the searchable list. */}
          <select
            className={cn(FORM_CONTROL, "lg:hidden w-full px-3 py-2.5")}
            aria-label="Employee"
            value={selectedId ?? ""}
            onChange={(e) => setSelectedId(Number(e.target.value))}
          >
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>

          <aside className="hidden lg:flex bg-surface border border-border rounded-xl flex-col min-h-0 max-h-[calc(100vh-14rem)]">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find employee…"
                  aria-label="Find employee"
                  className={cn(FILLED_INPUT, "w-full pl-9 pr-3 py-2")}
                />
              </div>
            </div>
            <div className="overflow-y-auto p-2 flex-1">
              {visible.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">No employees match “{query}”.</p>
              )}
              {visible.map((e) => {
                const active = e.id === selectedId;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedId(e.id)}
                    aria-pressed={active}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                      active ? "bg-primary-soft" : "hover:bg-surface-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold",
                        active ? "bg-primary text-primary-foreground" : "bg-surface-muted border border-border text-muted-foreground",
                      )}
                    >
                      {initials(e.name)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={cn("block text-sm truncate", active ? "font-semibold text-primary" : "font-medium text-foreground")}>
                        {e.name}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">{e.employee_code}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {selected && (
            <EmployeeEvaluationDetails
              key={selected.id}
              employeeId={selected.id}
              employeeName={selected.name}
              employeeSubtitle={`${selected.employee_code} · ${selected.email}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
